import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminClient } from "@/lib/api";
import { errText } from "@/lib/tournaments";

export default function ResultDialog({ match, scoring, onOpenChange, onSaved }) {
  const [sets, setSets] = useState([["", ""], ["", ""], ["", ""]]);
  const [outcome, setOutcome] = useState("normal");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!match) return;
    const s = (match.sets || []).map(([a, b]) => [String(a), String(b)]);
    while (s.length < 3) s.push(["", ""]);
    setSets(s); setOutcome(match.outcome || "normal"); setNote(match.note || "");
  }, [match]);
  if (!match) return null;
  const target = scoring?.set_points ?? 21, dec = scoring?.deciding_set_points ?? 15;
  const setVal = (i, j, v) => setSets((s) => s.map((row, k) => (k === i ? row.map((x, l) => (l === j ? v.replace(/\D/g, "") : x)) : row)));
  const wins = sets.reduce((acc, [a, b]) => (a !== "" && b !== "" ? [acc[0] + (+a > +b ? 1 : 0), acc[1] + (+b > +a ? 1 : 0)] : acc), [0, 0]);
  const needThird = wins[0] === 1 && wins[1] === 1;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { outcome, note: note || null, sets: sets.filter(([a, b]) => a !== "" && b !== "").map(([a, b]) => [Number(a), Number(b)]) };
      await adminClient.put(`/tournaments/admin/matches/${match.id}/result`, payload);
      toast.success("Το αποτέλεσμα αποθηκεύτηκε");
      onSaved();
    } catch (e) { toast.error(errText(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!match} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="result-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-black">Αποτέλεσμα #{match.number}</DialogTitle>
          <DialogDescription>{match.team_a_name} vs {match.team_b_name} · Σετ 1–2 στους {target}, 3ο σετ στους {dec}, διαφορά ≥{scoring?.min_margin ?? 2}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2 text-xs font-bold text-slate-600"><span className="truncate">{match.team_a_name}</span><span /><span className="truncate text-right">{match.team_b_name}</span></div>
          {sets.map((row, i) => {
            const disabled = outcome.startsWith("forfeit") || (i === 2 && !needThird && !(row[0] || row[1]));
            return (
              <div key={i} className={`grid grid-cols-[1fr_auto_1fr] gap-2 items-center ${disabled ? "opacity-50" : ""}`}>
                <Input inputMode="numeric" value={row[0]} disabled={disabled} onChange={(e) => setVal(i, 0, e.target.value)} data-testid={`set-${i + 1}-a`} className="rounded-xl text-center font-heading font-black text-lg h-12" />
                <span className="text-[10px] uppercase tracking-widest text-slate-500 w-14 text-center">Σετ {i + 1}{i === 2 ? ` (${dec})` : ""}</span>
                <Input inputMode="numeric" value={row[1]} disabled={disabled} onChange={(e) => setVal(i, 1, e.target.value)} data-testid={`set-${i + 1}-b`} className="rounded-xl text-center font-heading font-black text-lg h-12" />
              </div>
            );
          })}
          <div className="text-center font-heading font-black text-ocean" data-testid="result-sets-total">{wins[0]} – {wins[1]}</div>
          <select value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="result-outcome" className="w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
            <option value="normal">Κανονικός αγώνας</option>
            <option value="forfeit_a">Άνευ αγώνος — δεν εμφανίστηκε η {match.team_a_name}</option>
            <option value="forfeit_b">Άνευ αγώνος — δεν εμφανίστηκε η {match.team_b_name}</option>
            <option value="retired_a">Αποχώρηση/τραυματισμός — {match.team_a_name}</option>
            <option value="retired_b">Αποχώρηση/τραυματισμός — {match.team_b_name}</option>
          </select>
          {outcome !== "normal" && <p className="text-xs text-slate-500">{outcome.startsWith("forfeit") ? `Καταχωρείται ${target}–0, ${target}–0 υπέρ της αντίπαλης ομάδας.` : "Τα σετ που παίχτηκαν ισχύουν· τα υπόλοιπα κατακυρώνονται στην αντίπαλη ομάδα."}</p>}
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Σημείωση (προαιρετικό)" className="rounded-xl" data-testid="result-note" />
          <Button onClick={save} disabled={saving} data-testid="result-save" className="w-full rounded-full bg-ocean hover:bg-ocean-hover h-11">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Αποθήκευση αποτελέσματος"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
